import { Component, OnInit, ElementRef, ViewChildren, QueryList, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CartService } from '../../shared/services/cart.service';
import { CartSidebarService } from '../../shared/services/cart-sidebar.service';
import { CartSidebarComponent } from '../../core/components/cart-sidebar/cart-sidebar.component';
import { ShopService } from '../../shared/services/shop.service';
import { ProductService } from '../../shared/services/product.service';
import { StorageService } from '../../shared/services/storage.service';
import { ReferralTrackingService } from '../../shared/services/referral-tracking.service';
import { ShopProduct } from '../../shared/interfaces/shop-product.interface';
import { Observable, map, of, catchError, combineLatest } from 'rxjs';
import { VideoModalComponent } from '../../shared/components/video-modal/video-modal.component';
@Component({
  standalone: true,
    selector: 'app-shop',
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        CartSidebarComponent,
        MatDialogModule,
    ],
    templateUrl: './shop.component.html',
    styleUrls: ['./shop.component.scss']  
})
export class ShopComponent implements OnInit, AfterViewInit {
  products$: Observable<(ShopProduct & { productSlug?: string })[]>;
  expandedDescriptions: Map<string, boolean> = new Map<string, boolean>();
  descriptionNeedsButton: Map<string, boolean> = new Map<string, boolean>();
  
  @ViewChildren('descriptionText') descriptionElements!: QueryList<ElementRef>;

  constructor(
    public cartService: CartService,
    private cartSidebarService: CartSidebarService,
    private shopService: ShopService,
    private productService: ProductService,
    private storageService: StorageService,
    private referralTrackingService: ReferralTrackingService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    // Combine shop products with their corresponding product slugs
    this.products$ = combineLatest([
      this.shopService.getAllProducts(),
      this.productService.getAllProducts()
    ]).pipe(
      map(([shopProducts, products]) => {
        return shopProducts.map(shopProduct => {
          const matchingProduct = products.find(p => 
            p.title.toLowerCase() === shopProduct.name.toLowerCase()
          );
          return {
            ...shopProduct,
            productSlug: matchingProduct?.slug || this.generateSlug(shopProduct.name)
          };
        });
      })
    );
  }

  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[äöüß]/g, (match) => {
        const chars: { [key: string]: string } = {
          'ä': 'ae',
          'ö': 'oe',
          'ü': 'ue',
          'ß': 'ss'
        };
        return chars[match] || match;
      })
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  ngOnInit(): void {
    // Products are automatically loaded through the products$ observable
    // Referral tracking is handled automatically by ReferralTrackingService
    // which listens to router events and processes ?ref= parameters
  }
  
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.checkDescriptionLengths();
      this.cdr.detectChanges();
    });
    
    this.descriptionElements.changes.subscribe(() => {
      setTimeout(() => {
        this.checkDescriptionLengths();
        this.cdr.detectChanges();
      });
    });
  }
  
  checkDescriptionLengths(): void {
    this.descriptionElements.forEach((element: ElementRef) => {
      const el = element.nativeElement;
      const productId = el.dataset.productId;
      
      // Check if the content is overflowing the 3-line limit
      const isOverflowing = el.scrollHeight > el.clientHeight;
      this.descriptionNeedsButton.set(productId, isOverflowing);
    });
  }

  calculateMonthlyPrice(price: number): number {
    return price / 24;
  }

  addToCart(productId: string) {
    if (this.cartService.addToCart(productId)) {
      this.cartSidebarService.open();
    }
  }

  isInCart(productId: string): boolean {
    return this.cartService.isInCart(productId);
  }

  isDescriptionExpanded(productId: string): boolean {
    return this.expandedDescriptions.get(productId) === true;
  }

  toggleDescription(productId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.expandedDescriptions.set(productId, !this.isDescriptionExpanded(productId));
  }
  
  shouldShowButton(productId: string): boolean {
    return this.descriptionNeedsButton.get(productId) === true;
  }

  /**
   * Checks if a product is a Horizon Crypto product
   * @param product The product to check
   * @returns True if the product is a Horizon Crypto product
   */
  isHorizonCryptoProduct(product: ShopProduct): boolean {
    // Only show guarantee seal for specific crypto product IDs
    const cryptoProductIds = [
      'HX3c678iXajVcPzMZOrA',
      'IxCGZFrNU0WiPPqfpwiN'
    ];
    
    return cryptoProductIds.includes(product.id);
  }

  /**
   * Checks if a product is an Academy product
   * @param product The product to check
   * @returns True if the product name contains "academy"
   */
  isAcademyProduct(product: ShopProduct): boolean {
    return product.name.toLowerCase().includes('academy');
  }

  formatDescription(description: string): string {
    if (!description) return '';
    
    // Replace line breaks with <br> tags
    return description
      .replace(/\n/g, '<br>')
      // Replace bullet points with proper HTML
      .replace(/•\s/g, '<span class="bullet-point">•</span> ')
      // Make URLs clickable
      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  }

  openVideo(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Show loading indicator or placeholder while getting the URL
    const loadingDialog = this.dialog.open(VideoModalComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'video-modal-dialog',
      data: {
        videoUrl: '',
        title: 'Unsere Geld-zurück-Garantie',
        isLoading: true
      }
    });
    
    // Get the video URL from Firebase Storage
    this.storageService.getVideoUrl('videos/guarantee-explanation.mp4')
      .pipe(
        catchError(error => {
          console.error('Error loading video:', error);
          loadingDialog.close();
          // Show error dialog or message
          return of(null);
        })
      )
      .subscribe(url => {
        if (url) {
          // Close loading dialog
          loadingDialog.close();
          
          // Open video dialog with the actual URL
          this.dialog.open(VideoModalComponent, {
            width: '800px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            panelClass: 'video-modal-dialog',
            data: {
              videoUrl: url,
              title: 'Unsere Geld-zurück-Garantie'
            }
          });
        }
      });
  }

  openAcademyPdf(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Get the PDF URL from Firebase Storage and open it in a new tab
    this.storageService.getDownloadURL('documents/Academy-GzG.pdf')
      .pipe(
        catchError(error => {
          console.error('Error loading Academy PDF:', error);
          return of(null);
        })
      )
      .subscribe(url => {
        if (url) {
          // Open PDF in a new tab
          window.open(url, '_blank');
        }
      });
  }
}
